import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Activity, ArrowLeft, Lock, Eye, EyeOff, CheckCircle2, XCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  
  const token = searchParams.get('token') || '';
  const email = searchParams.get('email') || '';
  
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    // Validate token exists
    if (!token) {
      setError('Invalid or missing reset token. Please request a new password reset link.');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setIsLoading(true);
    
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token: token,
        email: email,
        new_password: formData.password
      });
      
      setSuccess(true);
      toast.success('Password reset successfully!');
      
      // Auto-redirect after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to reset password. The link may have expired.';
      toast.error(errorMsg);
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 p-12 flex-col justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center">
            <Activity className="w-6 h-6 text-white" strokeWidth={2} />
          </div>
          <div>
            <h1 className="text-white font-semibold text-xl tracking-tight font-[Manrope]">Pulse</h1>
            <p className="text-slate-500 text-sm">Players Health</p>
          </div>
        </div>
        
        <div>
          <h2 className="text-4xl font-bold text-white font-[Manrope] tracking-tight mb-4">
            Create New Password
          </h2>
          <p className="text-slate-400 text-lg leading-relaxed">
            Choose a strong password that you haven't used before to secure your account.
          </p>
        </div>

        <div className="text-slate-500 text-sm">
          © {new Date().getFullYear()} Players Health. All rights reserved.
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-lg bg-slate-900 flex items-center justify-center">
              <Activity className="w-6 h-6 text-white" strokeWidth={2} />
            </div>
            <div>
              <h1 className="text-slate-900 font-semibold text-xl tracking-tight font-[Manrope]">Pulse</h1>
              <p className="text-slate-500 text-sm">Players Health</p>
            </div>
          </div>

          {/* Error State */}
          {error && !success && (
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 font-[Manrope] mb-2">Link Expired</h2>
              <p className="text-slate-500 mb-6">{error}</p>
              <Button 
                onClick={() => navigate('/forgot-password')}
                className="w-full"
              >
                Request New Reset Link
              </Button>
              <div className="mt-4">
                <Link 
                  to="/login" 
                  className="inline-flex items-center text-sm text-slate-500 hover:text-slate-700"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Back to login
                </Link>
              </div>
            </div>
          )}

          {/* Success State */}
          {success && (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 font-[Manrope] mb-2">Password Reset!</h2>
              <p className="text-slate-500 mb-6">
                Your password has been successfully reset. Redirecting you to login...
              </p>
              <Button 
                onClick={() => navigate('/login')}
                className="w-full"
                data-testid="reset-goto-login-btn"
              >
                Go to Login
              </Button>
            </div>
          )}

          {/* Form State */}
          {!error && !success && (
            <>
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-slate-900 font-[Manrope]">Set New Password</h2>
                <p className="text-slate-500 mt-1">Your new password must be at least 8 characters</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="password">New Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      minLength={8}
                      className="pr-10"
                      data-testid="reset-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm New Password</Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      required
                      className="pr-10"
                      data-testid="reset-confirm-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={isLoading}
                  data-testid="reset-submit-btn"
                >
                  {isLoading ? 'Resetting...' : 'Reset Password'}
                  <Lock className="w-4 h-4 ml-2" />
                </Button>
              </form>

              <div className="mt-6 text-center">
                <Link 
                  to="/login" 
                  className="inline-flex items-center text-sm text-slate-500 hover:text-slate-700"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Back to login
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
